/**
 * [비즈니스 목적]
 * 경영정보 파일의 AI 기반 배치 변환 실행 페이지
 *
 * [워크플로우]
 * 1. 테넌트 선택 → 파일 온톨로지 목록 로드
 * 2. 파일별로 프롬프트 선택 (다중 선택 가능)
 * 3. 기준일시(BSDA) 설정 (단일 또는 범위)
 * 4. 변환 방식 선택: Python 스크립트 / Claude AI
 * 5. 배치 실행 → 진행률 표시 → 결과 확인
 *
 * [주요 기능]
 * - 파일별 독립 프롬프트 선택 (N:N 관계)
 * - 여러 날짜 일괄 변환 (배치 처리)
 * - Python 스크립트 변환 (고속, 검증 없음)
 * - 실시간 진행률 표시 (BatchProgressBar)
 * - 프롬프트 미리보기 (우측 패널)
 * - Python 스크립트 미리보기 (우측 패널)
 *
 * [변환 방식]
 * 1. Python 스크립트 변환 (conversionMode = 'python')
 *    - Python 스크립트만 실행 (검증 없음, executionMode = 'python_only')
 * 2. Claude AI 변환 (conversionMode = 'claude')
 *    - Claude Agent SDK를 사용한 지능형 변환
 *
 * [배치 실행 흐름]
 * - 파일 × 프롬프트 × 날짜 조합으로 배치 작업 생성
 * - 예: 파일 2개(각 3개 프롬프트) × 날짜 5개 = 30개 작업
 * - Cloud Tasks에 배치 등록 → 백그라운드 실행
 * - 결과는 BigQuery 테이블에 저장
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { PlayArrow as RunIcon } from '@mui/icons-material';
import { useGlobalTenant } from '../../contexts/GlobalTenantContext';
import { useAuth } from '../../contexts/AuthContext';

// Custom hooks
import { useFileOntology } from './hooks/useFileOntology';
import { useBatchExecution } from './hooks/useBatchExecution';

// Components
import FileSelectorWithPrompts from './components/FileSelectorWithPrompts';
import DateSelector from './components/DateSelector';
import BatchProgressBar from './components/BatchProgressBar';
import BatchResultsDrawer from './components/BatchResultsDrawer';
import PythonScriptPreview from './components/PythonScriptPreview';

// Utils
import { parseBsdaDates } from './utils/dateUtils';

const HyperloungeConverterPage = () => {
  const { selectedTenant, getSelectedTenantInfo, tenants, setSelectedTenant } = useGlobalTenant();
  const { idToken } = useAuth();

  // Get tenant info and extract tenant code
  const tenantInfo = getSelectedTenantInfo();
  const tenantCode = tenantInfo?.tenant_code || tenantInfo?.code || tenantInfo?.tenantCode || selectedTenant;

  // File ontology hook - Pass tenant CODE instead of tenant ID
  const { files, loading: filesLoading } = useFileOntology(tenantCode);

  // State
  const [filePromptMapping, setFilePromptMapping] = useState({});  // { fileId: promptId }
  const [bsdaDates, setBsdaDates] = useState('');
  const [executionMode] = useState('python_only');  // Fixed to 'python_only'
  const [showBatchConfirmDialog, setShowBatchConfirmDialog] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Fixed mode for backward compatibility
  const selectedMode = 'python';

  // Batch execution hook
  const {
    executing,
    batchProgress,
    batchTotal,
    batchResults,
    error,
    executeBatch,
    setError,
    cleanup,
    fallbackStatus,
    batchId,
    validationErrors
  } = useBatchExecution();

  // Cleanup on unmount
  useEffect(() => {
    return () => cleanup();
  }, [cleanup]);

  // Calculate total batch jobs
  const calculateBatchTotal = () => {
    const dateList = parseBsdaDates(bsdaDates);
    const selectedFileIds = Object.keys(filePromptMapping);
    if (selectedFileIds.length === 0 || dateList.length === 0) {
      return 0;
    }

    // 실제 job 개수 계산 (프롬프트 개수 고려)
    let total = 0;
    for (const fileId of selectedFileIds) {
      const promptIds = filePromptMapping[fileId];
      for (const bsdaValue of dateList) {
        if (!promptIds || promptIds.length === 0) {
          // 프롬프트 미선택 → 1개 job (모든 프롬프트 실행)
          total += 1;
        } else {
          // 선택된 프롬프트 개수만큼 job 생성
          total += promptIds.length;
        }
      }
    }
    return total;
  };

  // Handle batch execute button click
  const handleBatchExecute = () => {
    const total = calculateBatchTotal();
    if (total === 0) {
      setError('실행할 변환이 없습니다. 파일과 날짜를 확인해주세요.');
      return;
    }
    setShowBatchConfirmDialog(true);
  };

  // Execute batch conversion
  const handleConfirmBatchExecution = async () => {
    setShowBatchConfirmDialog(false);

    const dateList = parseBsdaDates(bsdaDates);

    const result = await executeBatch({
      selectedTenant: tenantCode,  // Use tenantCode instead of tenant_id
      filePromptMapping,  // Pass file-prompt mapping instead of selectedFiles
      dateList,
      selectedMode,   // Pass selected mode (python or text) - legacy
      conversionMode: 'python', // Fixed to python mode
      executionMode,  // Pass execution mode (python_only, python_gemini, full)
      files,
      idToken
    });

    if (result.success) {
      setShowResults(true);
    }
  };

  return (
    <Container maxWidth={false} sx={{ py: 2, px: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Hyperlounge Converter 배치 변환
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Python 스크립트를 사용한 고속 변환 (검증 없음)
        </Typography>
      </Box>

      {/* Tenant Selector */}
      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth size="small">
          <InputLabel>테넌트 선택</InputLabel>
          <Select
            value={selectedTenant}
            onChange={(e) => setSelectedTenant(e.target.value)}
            label="테넌트 선택"
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.id} value={tenant.id}>
                {tenant.tenant_name} ({tenant.tenant_codes?.find(tc => tc.is_primary)?.tenant_code || tenant.tenant_codes?.[0]?.tenant_code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Main Content */}
      <Grid container spacing={2}>
        {/* Left Panel: Configuration */}
        <Grid item xs={12} lg={6}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* File Selection with Per-File Prompt Selection */}
                <FileSelectorWithPrompts
                  files={files}
                  filePromptMapping={filePromptMapping}
                  onMappingChange={setFilePromptMapping}
                  disabled={executing}
                  loading={filesLoading}
                  selectedTenant={tenantCode}
                />

                {/* Execution Mode - Hidden (Fixed to python_only) */}

                {/* Date Selection */}
                <Divider sx={{ my: 1 }} />
                <DateSelector
                  selectedDates={bsdaDates}
                  onDatesChange={setBsdaDates}
                  disabled={executing}
                />

                {/* Error Display */}
                {error && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Batch Progress */}
                {executing && (
                  <BatchProgressBar progress={batchProgress} total={batchTotal} />
                )}

                {/* Claude Fallback Status - Hidden (python_only mode) */}

                {/* Batch ID & 검증 실패 사유 표시 */}
                {batchId && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ✅ Cloud Tasks 등록 완료
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Batch ID: {batchId}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                      백그라운드에서 변환이 진행됩니다. 결과는 BigQuery에서 확인하세요.
                    </Typography>
                  </Alert>
                )}

                {/* 검증 실패 사유 - Hidden (python_only mode) */}

                {/* Execution Button */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setFilePromptMapping({});
                      setBsdaDates('');
                    }}
                    disabled={executing}
                  >
                    선택 초기화
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={executing ? <CircularProgress size={20} /> : <RunIcon />}
                    onClick={handleBatchExecute}
                    disabled={
                      executing ||
                      !selectedTenant ||
                      Object.keys(filePromptMapping).length === 0 ||
                      parseBsdaDates(bsdaDates).length === 0
                    }
                  >
                    {executing ? '배치 변환 중...' : '배치 변환 실행'}
                  </Button>
                </Box>

                {/* Usage Guide */}
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="h5" gutterBottom fontWeight="bold">
                    사용 가이드
                  </Typography>
                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ lineHeight: 1.8 }}>
                    <Typography variant="body1" paragraph sx={{ mb: 3, lineHeight: 2 }}>
                      <strong>1. 파일 선택</strong><br />
                      • "전체 선택" 버튼으로 모든 파일을 한 번에 선택<br />
                      • 체크박스로 개별 파일 선택/해제
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ mb: 3, lineHeight: 2 }}>
                      <strong>2. 프롬프트 선택 (파일별)</strong><br />
                      • 각 파일마다 독립적으로 프롬프트 선택<br />
                      • 드롭다운에서 여러 프롬프트 동시 선택 가능<br />
                      • 선택하지 않으면 해당 파일은 변환 제외
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ mb: 3, lineHeight: 2 }}>
                      <strong>3. 기준일시 (BSDA) 설정</strong><br />
                      • 날짜 선택 후 "추가" 버튼 클릭<br />
                      • "날짜 범위 추가"로 여러 날짜 한 번에 추가<br />
                      • Chip의 X 버튼으로 개별 삭제
                    </Typography>

                    <Typography variant="body1" paragraph sx={{ lineHeight: 2 }}>
                      <strong>4. 배치 실행</strong><br />
                      • 각 파일의 선택된 프롬프트 × 날짜 = 총 변환 작업 수<br />
                      • 예: 파일 2개(각 3개 프롬프트) × 날짜 5개 = 30개 작업 실행<br />
                      • Python 스크립트를 사용하여 변환 실행<br />
                      • 진행률 표시 및 완료 후 로그 링크 제공
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Panel: Preview */}
        <Grid item xs={12} lg={6}>
          <Box sx={{ position: 'sticky', top: 16, height: 'calc(100vh - 100px)' }}>
            <PythonScriptPreview
              tenantCode={tenantCode}
              filePromptMapping={filePromptMapping}
              files={files}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Batch Confirmation Dialog */}
      <Dialog
        open={showBatchConfirmDialog}
        onClose={() => setShowBatchConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>배치 변환 확인</DialogTitle>
        <DialogContent>
          {/* 프롬프트 미선택 경고 */}
          {Object.values(filePromptMapping).some(p => !p || p.length === 0) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ⚠️ 프롬프트 미선택 파일이 있습니다!
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                프롬프트를 선택하지 않은 파일은 모든 연결된 프롬프트가 실행됩니다.
                <br/>
                미선택 파일: {Object.entries(filePromptMapping).filter(([_, p]) => !p || p.length === 0).map(([f]) => f).join(', ')}
              </Typography>
            </Alert>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              배치 변환 실행
            </Typography>
            <Typography variant="body2">
              총 <strong>{calculateBatchTotal()}개</strong>의 변환 작업이 순차적으로 실행됩니다.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Python 스크립트를 사용하여 변환합니다.
            </Typography>
          </Alert>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              변환 대상 상세:
            </Typography>
            <Box sx={{ pl: 2, maxHeight: 200, overflow: 'auto' }}>
              {Object.entries(filePromptMapping).map(([fileId, promptIds]) => {
                const fileInfo = files.find(f => f.file_id === fileId);
                const fileName = fileInfo?.displayName || fileId;
                const promptCount = promptIds?.length || 0;
                return (
                  <Typography key={fileId} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    • {fileName}: {promptCount > 0 ? `${promptCount}개 프롬프트` : '모든 프롬프트 (미선택)'}
                  </Typography>
                );
              })}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" color="text.secondary">
                날짜: {parseBsdaDates(bsdaDates).join(', ')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBatchConfirmDialog(false)} disabled={executing}>
            취소
          </Button>
          <Button
            onClick={handleConfirmBatchExecution}
            variant="contained"
            color="primary"
            disabled={executing}
          >
            실행 ({calculateBatchTotal()}개 job)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Drawer */}
      <BatchResultsDrawer
        open={showResults}
        onClose={() => setShowResults(false)}
        results={batchResults}
      />
    </Container>
  );
};

export default HyperloungeConverterPage;
